import { X } from "lucide-react";
import { Icon, Modal } from "@cntrl/ui";
import { usePlan } from "../../app/hooks/useBilling.js";
import { ProPlanBlock, ProPlanActions } from "./ProPlanBlock.js";
import { canChangePlan } from "./planFormat.js";
import styles from "./PlanDialog.module.scss";

/**
 * The plan-management dialog, opened by the Plan row's **Change plan** action (PO, 2026-09-10).
 *
 * It is a CHROME over {@link ProPlanBlock} / {@link ProPlanActions} and nothing else — the same body and the
 * same controls the roster's "Artist plan" tab mounts inline, because an org seat and a band seat looking at
 * one artist are looking at one subscription and a second copy is how two surfaces come to disagree about
 * what a band pays. The `SettingsModalShell` split, one level smaller.
 *
 * **HEADERLESS (PO, 2026-09-10).** The body opens with the Artist CNTRL Pro lockup, so a "PLAN" title bar
 * above it is chrome restating a heading the reader can already see — ruling 10's argument, applied to a
 * dialog whose body names itself. `hideHeader` keeps the title as an sr-only label so `aria-labelledby` still
 * resolves, and the dismiss moves to the corner the header's own would have occupied: `Modal` closes on
 * Escape and on the scrim, but a dialog whose only visible way out is a keystroke is the dead button this app
 * refuses elsewhere.
 *
 * **The controls are the FOOTER**, not trailing content: the primary move takes the card's trailing edge with
 * **Cancel subscription** left of it, which is the action row every other dialog in the app has. The footer
 * is passed only when the surface actually has controls — `Modal` draws the footer's bordered bar whenever
 * the prop is non-null, and a component that returns null is still a non-null element, so passing it
 * unconditionally leaves an empty ribbon under a band this seat may not act on (the `TeamModal` lesson).
 *
 * `bodyLayout` is left at the default INSET: this body is prose, not panes.
 */
export function PlanDialog({ bandId, bandName, onClose }: {
  bandId: string | undefined;
  /** The band whose subscription this is — the dialog can be opened with another artist in focus. */
  bandName?: string | null;
  onClose: () => void;
}) {
  const plan = usePlan(bandId);
  const actionable = canChangePlan(plan.data);

  return (
    <Modal
      title="Plan"
      onClose={onClose}
      size="md"
      hideHeader
      footer={actionable ? <ProPlanActions bandId={bandId} align="end" /> : undefined}
    >
      <button type="button" className={styles.close} aria-label="Close" onClick={onClose}>
        <Icon icon={X} size={20} />
      </button>
      <ProPlanBlock bandId={bandId} bandName={bandName} />
    </Modal>
  );
}
